/**
 * Password Change Form
 * Shared form (fields, validation, submit) used by both the voluntary
 * Profile > Security modal and the forced password-expiry page.
 */

import { useState } from 'react'
import { Lock, Eye, EyeOff, X, Check } from 'lucide-react'
import { useChangePassword } from '@/hooks/useProfile'
import { toast } from '@/lib/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface PasswordChangeFormProps {
  onSuccess: () => void
  onCancel?: () => void
  cancelLabel?: string
  cancelDisabled?: boolean
  submitLabel?: string
}

export function PasswordChangeForm({
  onSuccess,
  onCancel,
  cancelLabel = 'Cancel',
  cancelDisabled = false,
  submitLabel = 'Change Password',
}: PasswordChangeFormProps) {
  const changePassword = useChangePassword()

  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  })

  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
    // Clear error for this field
    setErrors(prev => ({
      ...prev,
      [e.target.name]: '',
    }))
  }

  const togglePasswordVisibility = (field: 'old' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field],
    }))
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.old_password) {
      newErrors.old_password = 'Current password is required'
    }

    if (!formData.new_password) {
      newErrors.new_password = 'New password is required'
    } else if (formData.new_password.length < 6) {
      newErrors.new_password = 'Password must be at least 6 characters'
    } else if (formData.new_password === formData.old_password) {
      newErrors.new_password = 'New password must be different from current password'
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your new password'
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      await changePassword.mutateAsync({
        old_password: formData.old_password,
        new_password: formData.new_password,
      })

      toast.success('Password changed successfully')

      // Reset form
      setFormData({
        old_password: '',
        new_password: '',
        confirm_password: '',
      })

      onSuccess()
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message
      if (errorMessage.toLowerCase().includes('incorrect')) {
        setErrors({ old_password: 'Current password is incorrect' })
      }
      toast.error('Failed to change password', errorMessage)
    }
  }

  // Password strength indicator
  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: 0, label: '', color: '' }
    if (password.length < 6) return { strength: 1, label: 'Weak', color: 'text-red-600' }
    if (password.length < 10) return { strength: 2, label: 'Medium', color: 'text-yellow-600' }
    return { strength: 3, label: 'Strong', color: 'text-green-600' }
  }

  const passwordStrength = getPasswordStrength(formData.new_password)

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Current Password */}
      <div className="space-y-2">
        <Label htmlFor="old_password">Current Password</Label>
        <div className="relative">
          <Input
            id="old_password"
            name="old_password"
            type={showPasswords.old ? 'text' : 'password'}
            value={formData.old_password}
            onChange={handleChange}
            className={errors.old_password ? 'border-red-500' : ''}
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('old')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPasswords.old ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.old_password && (
          <p className="text-sm text-red-600">{errors.old_password}</p>
        )}
      </div>

      {/* New Password */}
      <div className="space-y-2">
        <Label htmlFor="new_password">New Password</Label>
        <div className="relative">
          <Input
            id="new_password"
            name="new_password"
            type={showPasswords.new ? 'text' : 'password'}
            value={formData.new_password}
            onChange={handleChange}
            className={errors.new_password ? 'border-red-500' : ''}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('new')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {formData.new_password && (
          <div className="flex items-center gap-2 text-sm">
            <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full transition-all ${
                  passwordStrength.strength === 1
                    ? 'bg-red-600 w-1/3'
                    : passwordStrength.strength === 2
                    ? 'bg-yellow-600 w-2/3'
                    : 'bg-green-600 w-full'
                }`}
              />
            </div>
            <span className={passwordStrength.color}>{passwordStrength.label}</span>
          </div>
        )}
        {errors.new_password && (
          <p className="text-sm text-red-600">{errors.new_password}</p>
        )}
      </div>

      {/* Confirm Password */}
      <div className="space-y-2">
        <Label htmlFor="confirm_password">Confirm New Password</Label>
        <div className="relative">
          <Input
            id="confirm_password"
            name="confirm_password"
            type={showPasswords.confirm ? 'text' : 'password'}
            value={formData.confirm_password}
            onChange={handleChange}
            className={errors.confirm_password ? 'border-red-500' : ''}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('confirm')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {formData.confirm_password && formData.new_password === formData.confirm_password && (
          <p className="text-sm text-green-600 flex items-center gap-1">
            <Check className="h-3 w-3" />
            Passwords match
          </p>
        )}
        {errors.confirm_password && (
          <p className="text-sm text-red-600">{errors.confirm_password}</p>
        )}
      </div>

      {/* Password Requirements */}
      <div className="p-3 bg-gray-50 rounded-lg">
        <p className="text-sm font-medium text-gray-700 mb-2">Password requirements:</p>
        <ul className="text-sm text-gray-600 space-y-1">
          <li className="flex items-center gap-2">
            <Check className={`h-3 w-3 ${formData.new_password.length >= 6 ? 'text-green-600' : 'text-gray-400'}`} />
            At least 6 characters long
          </li>
          <li className="flex items-center gap-2">
            <Check className={`h-3 w-3 ${formData.new_password && formData.new_password !== formData.old_password ? 'text-green-600' : 'text-gray-400'}`} />
            Different from current password
          </li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={changePassword.isPending || cancelDisabled}
          >
            <X className="w-4 h-4 mr-2" />
            {cancelLabel}
          </Button>
        )}
        <Button
          type="submit"
          className="bg-primary"
          disabled={changePassword.isPending}
        >
          <Lock className="w-4 h-4 mr-2" />
          {changePassword.isPending ? 'Changing...' : submitLabel}
        </Button>
      </div>
    </form>
  )
}

export default PasswordChangeForm
